import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BadgeCheck,
  HeartHandshake,
  LockKeyhole,
  UserRound,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Brand,
  Button,
  ErrorState,
  Input,
  LoadingState,
  Textarea,
} from "../../design-system";
import { apiFetch, errorMessage } from "../../lib/api";

interface ReferralInfo {
  referral: { partnerName: string; partnerType: string; url: string };
  privacyDocumentVersion: string;
}

export function ReferralPage() {
  const { slug = "" } = useParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    expectation: "",
    marketing: false,
  });
  const referral = useQuery({
    queryKey: ["referral", slug],
    queryFn: () =>
      apiFetch<ReferralInfo>(`/public/referrals/${encodeURIComponent(slug)}`, {
        skipRefresh: true,
      }),
    retry: false,
  });
  const capture = useMutation({
    mutationFn: () =>
      apiFetch(`/public/referrals/${encodeURIComponent(slug)}/leads`, {
        method: "POST",
        skipRefresh: true,
        body: {
          name: form.name,
          email: form.email || undefined,
          phone: form.phone,
          expectation: form.expectation || undefined,
          consents: [
            {
              purpose: "LEAD_CONTACT",
              accepted: true,
              documentVersion: referral.data!.privacyDocumentVersion,
            },
            {
              purpose: "MARKETING",
              accepted: form.marketing,
              documentVersion: referral.data!.privacyDocumentVersion,
            },
          ],
        },
      }),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    capture.mutate();
  }
  if (referral.isLoading)
    return (
      <main className="centered-page">
        <LoadingState label="Preparando sua indicação…" />
      </main>
    );
  if (referral.isError || !referral.data)
    return (
      <main className="centered-page">
        <ErrorState retry={() => referral.refetch()} />
        <Link to="/">Voltar ao início</Link>
      </main>
    );
  if (capture.isSuccess)
    return (
      <main className="referral-page">
        <header>
          <Brand />
        </header>
        <section className="success-panel">
          <span>
            <BadgeCheck />
          </span>
          <h1>Recebemos seus dados!</h1>
          <p>
            A equipe da clínica entrará em contato. Sua indicação foi vinculada
            a <strong>{referral.data.referral.partnerName}</strong>.
          </p>
          <div className="success-panel__note">
            <HeartHandshake />
            <span>Obrigado por confiar nessa parceria.</span>
          </div>
        </section>
      </main>
    );

  return (
    <main className="referral-page">
      <header>
        <Brand />
        <span className="secure-note">
          <LockKeyhole size={15} /> Ambiente seguro
        </span>
      </header>
      <div className="referral-grid">
        <section className="referral-intro">
          <span className="referral-avatar">
            <UserRound />
          </span>
          <p className="eyebrow">Você foi indicado por</p>
          <h1>{referral.data.referral.partnerName}</h1>
          <p>
            Preencha seus dados para que a equipe da clínica possa entender seu
            momento e entrar em contato.
          </p>
          <div className="referral-promise">
            <BadgeCheck />
            <span>
              <strong>Atendimento personalizado</strong>
              <small>
                Seus dados serão usados apenas para as finalidades autorizadas.
              </small>
            </span>
          </div>
        </section>
        <section className="lead-form-card">
          <span className="eyebrow">Vamos conversar?</span>
          <h2>Conte um pouco sobre você</h2>
          {capture.isError && (
            <div className="alert alert--error" role="alert">
              {errorMessage(capture.error)}
            </div>
          )}
          <form onSubmit={submit} className="form-stack">
            <Input
              label="Nome completo"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Como podemos chamar você?"
            />
            <div className="form-grid">
              <Input
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="voce@exemplo.com"
              />
              <Input
                label="WhatsApp / telefone"
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(11) 99999-9999"
              />
            </div>
            <Textarea
              label="Qual é sua expectativa?"
              rows={3}
              value={form.expectation}
              onChange={(e) =>
                setForm({ ...form, expectation: e.target.value })
              }
              placeholder="Conte brevemente o que você procura"
            />
            <label className="check-row">
              <input type="checkbox" required />
              <span>
                Autorizo o uso dos meus dados para que a clínica entre em
                contato, conforme a Política de Privacidade.
              </span>
            </label>
            <label className="check-row check-row--optional">
              <input
                type="checkbox"
                checked={form.marketing}
                onChange={(e) =>
                  setForm({ ...form, marketing: e.target.checked })
                }
              />
              <span>
                Quero receber novidades e conteúdos da clínica (opcional).
              </span>
            </label>
            <Button type="submit" loading={capture.isPending}>
              Enviar meus dados <ArrowRight size={18} />
            </Button>
            <small className="form-privacy">
              <LockKeyhole size={13} /> Consentimento registrado conforme a
              LGPD. Versão {referral.data.privacyDocumentVersion}.
            </small>
          </form>
        </section>
      </div>
    </main>
  );
}
